/**
 * Состояние консоли.
 *
 * Одна точка правды на всё приложение: срез парка приходит по WebSocket,
 * выбор хостов и настройки вида живут локально. Экраны только читают это
 * состояние и дёргают действия — никакой своей загрузки данных у них нет.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode
} from 'react';
import type {
  Alert, FilePayloadItem, FleetResponse, Host, Job, JobKind, JobSummary,
  MetricPoint, ScheduledTask, Shell, Threshold
} from '@fleet/shared';
import { api, connectLive, setToken, token } from './api';
import { HOSTS, ON_HOSTS, plural } from './format';
import { THEMES, type ThemeKey } from './themes';

export type Screen =
  | 'dash' | 'fleet' | 'detail' | 'commands' | 'files' | 'rdp' | 'net' | 'tasks' | 'alerts';

export type Layout = 'cards' | 'table' | 'compact';
export type FilterKey = 'all' | 'warn' | 'hot' | 'offline' | 'sel';
export type BulkMode = 'instant' | 'staged';

export interface UploadedFile extends FilePayloadItem {
  size: string;
}

const PREFS_KEY = 'fleet.prefs';

interface Prefs {
  theme: ThemeKey;
  dense: boolean;
  layout: Layout;
  rdpCount: number;
  sync: boolean;
  shell: Shell;
  destPath: string;
  cmd: string;
}

const DEFAULT_PREFS: Prefs = {
  theme: 'cobalt',
  dense: false,
  layout: 'cards',
  rdpCount: 4,
  sync: true,
  shell: 'ps',
  destPath: '',
  cmd:
    'Get-CimInstance -Namespace root/wmi -ClassName MSAcpi_ThermalZoneTemperature |\n' +
    '  Select-Object -ExpandProperty CurrentTemperature'
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

/** Тема из localStorage — нужна экрану входа, который живёт вне провайдера. */
export function storedTheme(): ThemeKey {
  return loadPrefs().theme;
}

const EMPTY_FLEET: FleetResponse = {
  hosts: [], series: { avg: [], peak: [] }, alerts: [], jobs: [],
  schedule: [], thresholds: [], serverTime: '', demo: false,
  runtime: { parallel: 0, timeoutSec: 0, rdpAccount: '', offlineAfterSec: 0 }
};

export interface AppState {
  /* данные сервера */
  hosts: Host[];
  series: FleetResponse['series'];
  alerts: Alert[];
  jobs: JobSummary[];
  schedule: ScheduledTask[];
  thresholds: Threshold[];
  demo: boolean;
  runtime: FleetResponse['runtime'];
  connected: boolean;
  loading: boolean;
  error: string;

  /* состояние UI */
  screen: Screen;
  theme: ThemeKey;
  dense: boolean;
  layout: Layout;
  filter: FilterKey;
  search: string;
  sel: Record<string, true>;
  detailId: string | null;
  detailHistory: MetricPoint[];
  rdpCount: number;
  sync: boolean;
  shell: Shell;
  cmd: string;
  destPath: string;
  payload: UploadedFile[];
  bulkMode: BulkMode;
  wave: number;
  toast: string;
  job: Job | null;

  /* производные */
  selected: Host[];
  selCount: number;
  shown: Host[];
  hostById: (id: string) => Host | undefined;

  /* действия */
  go: (screen: Screen) => void;
  openHost: (id: string) => void;
  setTheme: (t: ThemeKey) => void;
  toggleDensity: () => void;
  setLayout: (l: Layout) => void;
  setFilter: (f: FilterKey) => void;
  setSearch: (s: string) => void;
  toggleHost: (id: string) => void;
  selectIds: (ids: string[]) => void;
  clearSel: () => void;
  setRdpCount: (n: number) => void;
  toggleSync: () => void;
  setShell: (s: Shell) => void;
  setCmd: (s: string) => void;
  setDestPath: (s: string) => void;
  addFiles: (files: FileList | File[]) => Promise<void>;
  removeFile: (sha: string) => void;
  setBulkMode: (m: BulkMode) => void;
  waveSlices: () => Host[][];
  runCommand: () => Promise<void>;
  runFiles: () => Promise<void>;
  runSpeed: () => Promise<void>;
  runReboot: () => Promise<void>;
  cancelRun: () => Promise<void>;
  muteAlert: (id: string) => Promise<void>;
  setTaskState: (id: string, state: 'active' | 'paused') => Promise<void>;
  runTask: (id: string) => Promise<void>;
  notify: (message: string) => void;
  logout: () => void;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp вне AppProvider');
  return ctx;
}

export function AppProvider({ children, onAuthLost }: { children: ReactNode; onAuthLost: () => void }) {
  const [data, setData] = useState<FleetResponse>(EMPTY_FLEET);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [screen, setScreen] = useState<Screen>('dash');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<Record<string, true>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailHistory, setDetailHistory] = useState<MetricPoint[]>([]);
  const [payload, setPayload] = useState<UploadedFile[]>([]);
  const [bulkMode, setBulkMode] = useState<BulkMode>('instant');
  const [wave, setWave] = useState(0);
  const [toast, setToast] = useState('');
  const [job, setJob] = useState<Job | null>(null);

  const jobIdRef = useRef<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patchPrefs = useCallback((patch: Partial<Prefs>) => {
    setPrefs(prev => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch {
        /* приватный режим — просто не запоминаем */
      }
      return next;
    });
  }, []);

  /** Короткое сообщение в углу. Прогон задачи гасит его сам, когда завершится. */
  const notify = useCallback((message: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(''), 4000);
  }, []);

  /* ─────────── первичная загрузка + живой канал ─────────── */

  useEffect(() => {
    let alive = true;
    api
      .fleet()
      .then(fleet => {
        if (!alive) return;
        setData(fleet);
        setLoading(false);
      })
      .catch((err: Error & { status?: number }) => {
        if (!alive) return;
        setLoading(false);
        if (err.status === 401) onAuthLost();
        else setError(err.message);
      });

    const live = connectLive(
      event => {
        if (!alive) return;
        if (event.type === 'snapshot') {
          setData(event.data);
          setLoading(false);
        } else if (event.type === 'hosts') {
          setData(prev => ({ ...prev, hosts: event.data }));
        } else if (event.type === 'alerts') {
          setData(prev => ({ ...prev, alerts: event.data }));
        } else if (event.type === 'jobs') {
          setData(prev => ({ ...prev, jobs: event.data }));
        } else if (event.type === 'job') {
          if (event.data.id === jobIdRef.current) {
            setJob(event.data);
            if (event.data.state !== 'running') setToast('');
          }
        }
      },
      setConnected
    );

    return () => {
      alive = false;
      live.close();
    };
  }, [onAuthLost]);

  /* ─────────── история метрик открытого хоста ─────────── */

  useEffect(() => {
    if (screen !== 'detail' || !detailId) return;
    let alive = true;
    const pull = () => {
      api
        .host(detailId)
        .then(res => alive && setDetailHistory(res.history))
        .catch(() => undefined);
    };
    pull();
    const t = setInterval(pull, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [screen, detailId]);

  /* ─────────── производные ─────────── */

  const hosts = data.hosts;

  const hostById = useCallback((id: string) => hosts.find(h => h.id === id), [hosts]);

  const selected = useMemo(() => hosts.filter(h => sel[h.id]), [hosts, sel]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hosts.filter(h => {
      if (
        q &&
        !(
          h.name.toLowerCase().includes(q) ||
          h.ip.includes(q) ||
          h.role.toLowerCase().includes(q) ||
          h.loc.toLowerCase().includes(q)
        )
      ) {
        return false;
      }
      if (filter === 'warn') return h.state === 'warn';
      if (filter === 'offline') return h.state === 'offline';
      if (filter === 'hot') return h.temp > 82;
      if (filter === 'sel') return !!sel[h.id];
      return true;
    });
  }, [hosts, search, filter, sel]);

  /* ─────────── действия ─────────── */

  const go = useCallback((next: Screen) => setScreen(next), []);

  const openHost = useCallback((id: string) => {
    setDetailId(id);
    setDetailHistory([]);
    setScreen('detail');
  }, []);

  const toggleHost = useCallback((id: string) => {
    setSel(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  }, []);

  const selectIds = useCallback((ids: string[]) => {
    setSel(prev => {
      const next = { ...prev };
      for (const id of ids) next[id] = true;
      return next;
    });
  }, []);

  const clearSel = useCallback(() => setSel({}), []);

  const waveSlices = useCallback((): Host[][] => {
    const arr = selected;
    const a = Math.max(1, Math.round(arr.length * 0.05));
    const b = Math.max(1, Math.round(arr.length * 0.25));
    return [arr.slice(0, a), arr.slice(a, a + b), arr.slice(a + b)];
  }, [selected]);

  const start = useCallback(
    async (kind: JobKind, targets: Host[], label: string, extra: Record<string, unknown> = {}) => {
      if (!targets.length) {
        notify('Не выбрано ни одного хоста');
        return;
      }
      const screenFor: Screen = kind === 'files' ? 'files' : kind === 'speed' ? 'net' : 'commands';
      setScreen(screenFor);
      try {
        const created = await api.createJob({
          kind,
          hostIds: targets.map(h => h.id),
          shell: prefs.shell,
          ...extra
        });
        jobIdRef.current = created.id;
        setJob(created);
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast(label);
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Не удалось запустить задачу');
      }
    },
    [notify, prefs.shell]
  );

  const runCommand = useCallback(async () => {
    const staged = bulkMode === 'staged';
    const targets = staged ? waveSlices()[wave] ?? [] : selected;
    await start('cmd', targets, 'Запуск на ' + plural(targets.length, ON_HOSTS), {
      command: prefs.cmd,
      wave: staged ? wave + 1 : undefined
    });
    if (staged && wave < 2) setWave(w => w + 1);
  }, [bulkMode, wave, waveSlices, selected, start, prefs.cmd]);

  const runFiles = useCallback(async () => {
    await start('files', selected, 'Передача запущена на ' + plural(selected.length, ON_HOSTS), {
      destPath: prefs.destPath,
      files: payload.map(({ name, sizeBytes, kind, sha256 }) => ({ name, sizeBytes, kind, sha256 })),
      runAfter: payload.some(f => f.name.toLowerCase().endsWith('install.ps1')) ? 'install.ps1' : undefined
    });
  }, [selected, start, prefs.destPath, payload]);

  const runSpeed = useCallback(async () => {
    const targets = selected.length ? selected : hosts.filter(h => h.state !== 'offline').slice(0, 16);
    await start('speed', targets, 'Speedtest запущен на ' + plural(targets.length, ON_HOSTS));
  }, [selected, hosts, start]);

  const runReboot = useCallback(async () => {
    await start('reboot', selected, 'Перезагрузка на ' + plural(selected.length, ON_HOSTS));
  }, [selected, start]);

  const cancelRun = useCallback(async () => {
    const id = jobIdRef.current;
    setToast('');
    if (!id) return;
    try {
      const cancelled = await api.cancelJob(id);
      setJob(cancelled);
    } catch {
      /* задача уже успела завершиться — отменять нечего */
    }
  }, []);

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;
      notify(`Загружаю ${plural(list.length, ['файл', 'файла', 'файлов'])}…`);
      try {
        const uploaded = await Promise.all(list.map(f => api.upload(f)));
        setPayload(prev => {
          const bySha = new Map(prev.map(f => [f.sha256, f]));
          for (const f of uploaded) bySha.set(f.sha256, f);
          return [...bySha.values()];
        });
        notify(`Готово: ${plural(uploaded.length, ['файл', 'файла', 'файлов'])} в пакете`);
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Не удалось загрузить файлы');
      }
    },
    [notify]
  );

  const removeFile = useCallback((sha: string) => {
    setPayload(prev => prev.filter(f => f.sha256 !== sha));
  }, []);

  const muteAlert = useCallback(
    async (id: string) => {
      try {
        await api.muteAlert(id, 2);
        setData(prev => ({ ...prev, alerts: prev.alerts.filter(a => a.id !== id) }));
        notify('Инцидент заглушён на 2 часа');
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Не удалось заглушить');
      }
    },
    [notify]
  );

  const setTaskState = useCallback(
    async (id: string, state: 'active' | 'paused') => {
      try {
        await api.setTaskState(id, state);
        setData(prev => ({
          ...prev,
          schedule: prev.schedule.map(t => (t.id === id ? { ...t, state } : t))
        }));
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Не удалось изменить задачу');
      }
    },
    [notify]
  );

  const runTask = useCallback(
    async (id: string) => {
      try {
        const { jobId } = await api.runTask(id);
        jobIdRef.current = jobId;
        setJob(await api.job(jobId));
        notify('Задача запущена');
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Не удалось запустить задачу');
      }
    },
    [notify]
  );

  const logout = useCallback(() => {
    setToken('');
    onAuthLost();
  }, [onAuthLost]);

  const value: AppState = {
    hosts,
    series: data.series,
    alerts: data.alerts,
    jobs: data.jobs,
    schedule: data.schedule,
    thresholds: data.thresholds,
    demo: data.demo,
    runtime: data.runtime,
    connected,
    loading,
    error,

    screen,
    theme: prefs.theme,
    dense: prefs.dense,
    layout: prefs.layout,
    filter,
    search,
    sel,
    detailId,
    detailHistory,
    rdpCount: prefs.rdpCount,
    sync: prefs.sync,
    shell: prefs.shell,
    cmd: prefs.cmd,
    destPath: prefs.destPath,
    payload,
    bulkMode,
    wave,
    toast,
    job,

    selected,
    selCount: selected.length,
    shown,
    hostById,

    go,
    openHost,
    setTheme: t => patchPrefs({ theme: t }),
    toggleDensity: () => patchPrefs({ dense: !prefs.dense }),
    setLayout: l => {
      patchPrefs({ layout: l });
      setScreen('fleet');
    },
    setFilter,
    setSearch,
    toggleHost,
    selectIds,
    clearSel,
    setRdpCount: n => patchPrefs({ rdpCount: n }),
    toggleSync: () => patchPrefs({ sync: !prefs.sync }),
    setShell: s => patchPrefs({ shell: s }),
    setCmd: s => patchPrefs({ cmd: s }),
    setDestPath: s => patchPrefs({ destPath: s }),
    addFiles,
    removeFile,
    setBulkMode: m => {
      setBulkMode(m);
      setWave(0);
    },
    waveSlices,
    runCommand,
    runFiles,
    runSpeed,
    runReboot,
    cancelRun,
    muteAlert,
    setTaskState,
    runTask,
    notify,
    logout
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Тема как объект — экраны берут из неё цвета тепловой карты. */
export function useTheme() {
  const { theme } = useApp();
  return THEMES[theme];
}
